import type Account from '@/data/account/model';
import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import type User from '@/data/user/model';
import useDialog from '@/dialogs/hooks/useDialog';
import { setEmployeeOnboardingItemCompleted } from '@/onboarding/actions/setEmployeeOnboardingItemCompleted';
import { EmployeeOnboardingItems, EmployeeOnboardingStatus } from '@/onboarding/constants';
import GuideHighlight from '@/onboarding/modules/guides/components/GuideHighlight';
import { setEmployeeOnboardingStatus } from '@/onboarding/reducer';
import { getEmployeeOnboardingStatus } from '@/onboarding/selectors';
import { CREATE_REQUEST_DIALOG, EDIT_REQUEST_DIALOG } from '@/requests/modules/timeoff/dialogs';
import { SCHEDULER_ONINTERACTION } from '@/scheduler/util/constants';
import { MenuItem } from '@/shared/components/nav/MenuItem';
import MenuItems from '@/shared/components/nav/MenuItems';
import Features from '@/shared/features';
import Mercury from '@/shared/util/mercury';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwDispatch, useWiwSelector } from '@/store';

type RequestsMenuItemProps = {
  requestsEnabled: boolean;
  schedulerEnabled: boolean;
  swapsEnabled: boolean;
  authUser: User;
  authAccount: Account;
  featureOpenShiftApproval: string;
};

type OpenDialogType = {
  persist?: () => void;
  preventDefault: () => void;
};

type TrackClickType =
  | 'request_time_off'
  | 'view_time_off_requests'
  | 'shift_requests'
  | 'openshift_requests'
  | 'absences';

const RequestsMenuItem = ({
  authUser,
  authAccount,
  featureOpenShiftApproval,
  requestsEnabled,
  schedulerEnabled,
  swapsEnabled,
}: RequestsMenuItemProps) => {
  const dispatch = useWiwDispatch();
  const { openDialog } = useDialog();

  const at383EditingTimeoff = useLDFlag('at-383-requests-editing');
  const TIME_OFF_DIALOG = at383EditingTimeoff ? EDIT_REQUEST_DIALOG : CREATE_REQUEST_DIALOG;

  const employeeOnboardingStatus = useWiwSelector(getEmployeeOnboardingStatus);
  const isTimeOffGuidedStep = employeeOnboardingStatus === EmployeeOnboardingStatus.TimeOff;

  const dismissGuide = () => {
    dispatch(setEmployeeOnboardingStatus(EmployeeOnboardingStatus.Idle));
  };

  const completeGuide = () => {
    dispatch(setEmployeeOnboardingStatus(EmployeeOnboardingStatus.Idle));
    dispatch(setEmployeeOnboardingItemCompleted(EmployeeOnboardingItems.TimeOffCompleted));
    posthogCapture('employee_onboarding.time_off_requests.click.request_timeoff');
  };

  const handleClick = (event: OpenDialogType) => {
    if (event?.persist) {
      event.persist();
    }

    event.preventDefault();
    trackClick('request_time_off');
    openDialog(TIME_OFF_DIALOG);
  };

  const trackClick = (type: TrackClickType) => {
    posthogCapture(`top_nav.requests.${type}`);

    if (type === 'openshift_requests') {
      Mercury.track(
        'scheduler::onInteraction',
        {
          ...SCHEDULER_ONINTERACTION,
          target: 'openshiftRequestsNav',
          category: 'navigation',
          userRole: authUser.mercuryRoleName(),
        },
        {},
      );
    }
  };

  const renderAbsenceMenuItem = () => {
    if (
      !authAccount.hasFeature(Features.ABSENCE_TRACKING, true) ||
      !authAccount.getSettings('schedule.track_absences', false)
    ) {
      return null;
    }

    return (
      <MenuItem
        data-testid="top-nav-absences"
        onClick={() => trackClick('absences')}
        to="/requests/absences"
        icon="requests-absences"
        label="Absences"
      />
    );
  };

  const withDialog = () => {
    return (
      <GuideHighlight
        isActive={isTimeOffGuidedStep}
        highlightId="timeoff-guide-highlight"
        onHighlightClick={completeGuide}
        showPopover
        popoverText="Click “Request Time-off” to create a time off request."
        popoverPosition="left-start"
        popoverOffset={{ mainAxis: 10, crossAxis: 10 }}
        hideOverlay
        dismissOnOutsideClick
        onDismiss={dismissGuide}
      >
        <MenuItem
          to="#"
          icon="add"
          data-testid="top-nav-request-time-off"
          label="Request Time-Off"
          onClick={(event: Event) => handleClick(event)}
        />
      </GuideHighlight>
    );
  };

  return (
    <MenuItem
      className="menu-icon"
      icon="requests"
      label="Requests"
      hideLabel="desktop"
      dropdown
      forceActive={isTimeOffGuidedStep}
    >
      <MenuItems dropdownMenu>
        {(requestsEnabled || authUser.canSupervise()) && withDialog()}
        <MenuItem
          data-testid="top-nav-view-time-off-requests"
          onClick={() => trackClick('view_time_off_requests')}
          to="/requests/time-off"
          icon="requests-timeoff"
          label="Time Off Requests"
        />
        {schedulerEnabled && swapsEnabled && (
          <MenuItem
            data-testid="top-nav-shift-requests"
            onClick={() => trackClick('shift_requests')}
            to="/requests/shift"
            icon="requests-shifts"
            label="Shift Requests"
          />
        )}
        {authAccount.hasFeature(featureOpenShiftApproval, true) && (
          <MenuItem
            data-testid="top-nav-shift-requests"
            icon="requests-openshifts"
            label="OpenShift Requests"
            onClick={() => trackClick('openshift_requests')}
            to="/requests/openshift"
          />
        )}
        {renderAbsenceMenuItem()}
      </MenuItems>
    </MenuItem>
  );
};

export default RequestsMenuItem;
